import Input from '@components/ui-old/input';
import Button from '@components/ui-old/button';
import { useForm } from 'react-hook-form';
import TextArea from '@components/ui-old/text-area';
import { useTranslation } from 'next-i18next';
import { useFeedback } from '@framework/customer/use-feedback';
import { useState } from 'react';
import { ToastProvider, Toast, ToastTitle, ToastClose, ToastViewport } from '@/components/ui/toast';
import { CircleCheckIcon, XIcon } from 'lucide-react';
import { useProgressTimer } from '@/hooks/use-progress-timer';
interface ContactFormValues {
  firstName: string;
  email: string;
  lastName: string;
  phone: string;
  message: string;
}
const ContactForm: React.FC = () => {
  const {
    register,
    handleSubmit,
    formState: {
      errors
    },
    reset
  } = useForm<ContactFormValues>();
  const {
    t
  } = useTranslation();
  const feedbackMutation = useFeedback();

  // Toast notifications state/hooks
  const [toastOpen, setToastOpen] = useState(false);
  const toastDuration = 3000;
  const {
    progress,
    start,
    pause,
    resume
  } = useProgressTimer({
    duration: toastDuration,
    onComplete: () => setToastOpen(false)
  });
  const [toastMessage, setToastMessage] = useState<string>('');
  const [toastType, setToastType] = useState<'success' | 'error'>('success');
  const onSubmit = async (values: ContactFormValues) => {
    try {
      await feedbackMutation.mutateAsync(values);
      setToastType('success');
      setToastMessage(t('common:text-feedback-success'));
      setToastOpen(true);
      start();
      reset();
    } catch (error: any) {
      setToastType('error');
      setToastMessage(error.response?.data?.message ?? t('common:error-canvas-context'));
      setToastOpen(true);
      start();
    }
  };
  return <ToastProvider swipeDirection="right" data-sentry-element="ToastProvider" data-sentry-component="ContactForm" data-sentry-source-file="contact-form.tsx">
      <>
        <form onSubmit={handleSubmit(onSubmit)} className="w-full mx-auto flex flex-col justify-center" noValidate>
          <div className="flex flex-col space-y-5">
            <div className="flex flex-col md:flex-row space-y-5 md:space-y-0">
              <Input labelKey="forms:label-first-name" placeholderKey="forms:placeholder-name" {...register('firstName', {
              required: t('forms:first-name-required')
            })} className="w-full md:w-1/2" errorKey={errors.firstName?.message} variant="solid" data-sentry-element="Input" data-sentry-source-file="contact-form.tsx" />
              <Input labelKey="forms:label-last-name" placeholderKey="forms:placeholder-name" {...register('lastName', {
              required: t('forms:last-name-required')
            })} className="w-full md:w-1/2 ltr:md:ml-2.5 rtl:md:mr-2.5 ltr:lg:ml-5 rtl:lg:mr-5 mt-2 md:mt-0" errorKey={errors.lastName?.message} variant="solid" data-sentry-element="Input" data-sentry-source-file="contact-form.tsx" />
            </div>
            <div className="flex flex-col md:flex-row space-y-5 md:space-y-0">
              <Input labelKey="forms:label-email-star" type="email" placeholderKey="forms:placeholder-email" {...register('email', {
              required: t('forms:email-required'),
              pattern: {
                value: /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
                message: t('forms:email-error')
              }
            })} className="w-full md:w-1/2" errorKey={errors.email?.message} variant="solid" data-sentry-element="Input" data-sentry-source-file="contact-form.tsx" />
              <Input labelKey="forms:label-phone" {...register('phone', {
              required: t('forms:phone-required')
            })} className="w-full md:w-1/2 ltr:md:ml-2.5 rtl:md:mr-2.5 ltr:lg:ml-5 rtl:lg:mr-5 mt-2 md:mt-0" placeholderKey="forms:placeholder-phone" errorKey={errors.phone?.message} variant="solid" data-sentry-element="Input" data-sentry-source-file="contact-form.tsx" />
            </div>
            <TextArea labelKey="forms:label-message" {...register('message', {
            required: t('forms:message-required')
          })} className="relative mb-4" placeholderKey="forms:placeholder-message" data-sentry-element="TextArea" data-sentry-source-file="contact-form.tsx" />
            <div className="relative">
              <Button type="submit" className="h-12 lg:h-14 mt-1 text-sm lg:text-base w-full sm:w-auto" disabled={feedbackMutation.isLoading} data-sentry-element="Button" data-sentry-source-file="contact-form.tsx">
                {feedbackMutation.isLoading ? t('common:button-sending') : t('common:button-send-message')}
              </Button>
            </div>
          </div>
        </form>
      </>
      <Toast open={toastOpen} onOpenChange={open => {
      setToastOpen(open);
      if (open) start();
    }} onPause={pause} onResume={resume} data-sentry-element="Toast" data-sentry-source-file="contact-form.tsx">
        <div className="flex w-full justify-between gap-3">
          {toastType === 'success' ? <CircleCheckIcon size={16} className="mt-0.5 text-emerald-500" /> : <XIcon size={16} className="mt-0.5 text-red-500" />}
          <div className="flex flex-col flex-grow gap-3">
            <ToastTitle data-sentry-element="ToastTitle" data-sentry-source-file="contact-form.tsx">{toastMessage}</ToastTitle>
          </div>
          <ToastClose data-sentry-element="ToastClose" data-sentry-source-file="contact-form.tsx">
            <button className="p-1"><XIcon size={16} data-sentry-element="XIcon" data-sentry-source-file="contact-form.tsx" /></button>
          </ToastClose>
        </div>
        <div className={`absolute -left-2 right-0 bottom-0 h-1 ${toastType === 'success' ? 'bg-emerald-500' : 'bg-red-500'}`} style={{
        width: `${progress / toastDuration * 100}%`,
        transition: 'width 100ms linear'
      }} />
      </Toast>
      <ToastViewport className="fixed top-4 right-4 z-[9999] sm:bottom-4 sm:top-auto" data-sentry-element="ToastViewport" data-sentry-source-file="contact-form.tsx" />
    </ToastProvider>;
};
export default ContactForm;